import { ChartInfoKind } from '@frontend/chart/types';
import { EntityType, formatTimeDifference } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { StatisticsCard } from '../../shared';
import { useLendingLongestInUseEquipmentsListCard } from './use-lending-longest-in-use-equipments-list-card';

export interface LendingLongestInUseEquipmentsListCardProps {
    accountId: string;
}

export const LendingLongestInUseEquipmentsListCard = (props: LendingLongestInUseEquipmentsListCardProps) => {
    const { items, isLoading } = useLendingLongestInUseEquipmentsListCard(props);
    const now = new Date().toISOString();

    return (
        <StatisticsCard
            infoKind={ChartInfoKind.LONGEST_IN_USE_EQUIPMENTS_LIST}
            title={
                <FormattedMessage
                    id='platform.statistics.lending.lending-longest-in-use-equipments-list-card.title'
                    description='Title of the lending dashboard list showing equipments that have been in use the longest without being returned'
                    defaultMessage='Longest in use'
                />
            }
            subtitle={
                <FormattedMessage
                    id='platform.statistics.lending.lending-longest-in-use-equipments-list-card.subtitle'
                    description='Subtitle of the lending dashboard list showing equipments still outstanding and not yet returned'
                    defaultMessage='Not returned yet'
                />
            }
            isLoading={isLoading}
            hasData={items.length > 0}>
            <div className='min-h-0 flex-1 overflow-y-auto'>
                <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                    <div className='w-full px-4 py-3'>{CommonMessage.OBJECTS.EQUIPMENT.DEFAULT}</div>
                    <div className='w-[140px] shrink-0 px-4 py-3'>{CommonMessage.OBJECTS.USER.DEFAULT}</div>
                    <div className='w-[100px] shrink-0 px-4 py-3 text-right'>{CommonMessage.FORMS.DURATION}</div>
                </div>
                {items.map((item) => (
                    <div
                        key={item.id}
                        className='border-border-default flex h-11 items-center gap-4 border-b'>
                        <div className='text-foreground-default flex w-full min-w-0 items-center px-4 text-sm font-medium'>
                            {item.product ? (
                                <PlatformObjectBadge
                                    display={item.product.name}
                                    entityType={item.product.entity_type}
                                    id={item.product.entity_id}
                                    foreign={item.product.foreign}
                                    accountId={accountIdFromResolvedEntity(item.product)}
                                />
                            ) : (
                                <PlatformObjectBadge
                                    display={item.serial_number}
                                    entityType={EntityType.EQUIPMENT}
                                    id={item.id}
                                />
                            )}
                        </div>
                        <div className='flex w-[140px] shrink-0 items-center px-4'>
                            {item.user ? (
                                <PlatformObjectBadge
                                    display={item.user.name}
                                    entityType={item.user.entity_type}
                                    id={item.user.entity_id}
                                    foreign={item.user.foreign}
                                    accountId={accountIdFromResolvedEntity(item.user)}
                                />
                            ) : (
                                <span className='text-foreground-muted text-sm'>/</span>
                            )}
                        </div>
                        <div className='flex w-[100px] shrink-0 justify-end pe-4'>
                            <span className='text-background-surface bg-status-warning inline-flex min-w-8 items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'>
                                {formatTimeDifference(item.update_timestamp, now)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </StatisticsCard>
    );
};
